import translate from '@allesauto/i18n';
import { useWindowSize } from '@allesauto/utils/hooks';
import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import FilterBoxCarTypesList from './FilterBoxCarTypesList';
import {
  BaseColors,
  DisplayValueTypes,
  FilterBoxCarTypesBackgroundColor,
  ICarTypesProps,
  IFilterBoxCarTypesProps,
} from './types';
// large screen from screens.json
const MAX_WIDTH = 1200;
const DEFAULT_MAX_NUMBER_OF_ITEMS = 6;

export type {
  BaseColors,
  DisplayValueTypes,
  FilterBoxCarTypesBackgroundColor,
  ICarTypesProps,
  IFilterBoxCarTypesProps,
};
export default function FilterBoxCarTypes({
  heading,
  filterCarTypeItems,
  items,
  background = BaseColors.WHITE,
  priceFontColor = FilterBoxCarTypesBackgroundColor.GREY,
  description = '',
  displayValue = DisplayValueTypes.startingPrice,
}: IFilterBoxCarTypesProps) {
  const [maxNumberOfItems, setMaxNumberOfItems] = useState(items.length);
  const [isViewMore, setIsViewMore] = useState(true);

  const [width] = useWindowSize();
  const intl = useIntl();
  const classNames = {
    WHITE: {
      container: 'flex flex-col bg-white px-40px py-60px',
      heading:
        'break-words font-headline font-weight-bold line-clamp text-ellipsis text-headline3 mb-20px text-black',
      description: 'pb-20px lg:w-[50%] sm:w-[100%] text-100',
      itemContainer: 'gap-15px grid sm:grid-cols-2 lg:grid-cols-5',
      viewMore:
        'lg:hidden flex items-center self-center underline pt-10px text-purple80',
    },
    GREY: {
      container: 'flex flex-col bg-grey10 px-40px py-60px',
      heading:
        'break-words font-headline font-weight-bold line-clamp text-ellipsis text-headline3 mb-20px text-black',
      description: 'pb-20px lg:w-[50%] sm:w-[100%] text-grey-100',
      itemContainer: 'gap-15px grid sm:grid-cols-2 lg:grid-cols-5',
      viewMore:
        'lg:hidden flex items-center self-center underline pt-10px text-purple80',
    },
  };

  useEffect(() => {
    const isSmallScreen = width < MAX_WIDTH;
    setIsViewMore(true);
    setMaxNumberOfItems(
      isSmallScreen ? DEFAULT_MAX_NUMBER_OF_ITEMS : items.length
    );
  }, [items.length, width]);

  function handleViewMode() {
    isViewMore
      ? setMaxNumberOfItems(items.length)
      : setMaxNumberOfItems(DEFAULT_MAX_NUMBER_OF_ITEMS);
    setIsViewMore(!isViewMore);
  }

  function getItem(id: string) {
    const item = filterCarTypeItems.find((value) => value._uid === id);
    return item;
  }

  function getPrice(id?: string) {
    if (!id) return 0;

    const item = getItem(id);

    return item?.price ?? 0;
  }
  function getOffersCount(id?: string) {
    if (!id) return 0;
    const item = getItem(id);

    return item?.offersAmount ?? 0;
  }
  return (
    <div className={classNames[background].container}>
      <div className={classNames[background].heading}>{heading}</div>
      <div className={classNames[background].description}>{description}</div>
      <div className={classNames[background].itemContainer}>
        {items.slice(0, maxNumberOfItems).map((item) => (
          <FilterBoxCarTypesList
            key={item._uid}
            carType={item.carType}
            description={item.description}
            price={getPrice(item._uid)}
            priceFontColor={priceFontColor}
            searchUrl={item.searchUrl}
            offersAmount={getOffersCount(item._uid)}
            displayValue={displayValue}
          />
        ))}
      </div>
      <div className={classNames[background].viewMore}>
        {isViewMore ? <ChevronDown16 /> : <ChevronUp16 />}
        <button onClick={handleViewMode}>
          <div className="pl-5px">
            {isViewMore
              ? translate(intl, 'ui.FILTER_PAGE.SHOW_MORE')
              : translate(intl, 'ui.FILTER_PAGE.SHOW_LESS')}
          </div>
        </button>
      </div>
    </div>
  );
}
